import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';

const Transfer = graphql(`
  mutation TransferTeam($id: uuid!, $newOwnerId: String!) {
    transfer_team(args: { team_id: $id, new_owner_id: $newOwnerId }) {
      id
    }
  }
`);

const GetContext = graphql(`
  query GetTeamTransferConfirmContext($id: uuid!, $personId: String!) {
    team_by_pk(id: $id) {
      id
      title
    }
    person_by_pk(id: $personId) {
      id
      name
    }
  }
`);

export default function TeamTransferConfirm() {
  const { teamId, transferId } = useLocalSearchParams<{
    teamId: string;
    transferId: string;
  }>();
  const { back } = useRouter();
  const [, transfer] = useMutation(Transfer);
  const [result, refresh] = useQuery({
    query: GetContext,
    variables: { id: teamId, personId: transferId },
  });
  const entity = result.data?.team_by_pk;
  const recipient = result.data?.person_by_pk;

  const onTransfer = async () => {
    const result = await transfer({ id: teamId, newOwnerId: transferId });
    if (result.error || !result.data?.transfer_team?.id)
      return { success: false };
    return { success: true };
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: entity?.title ? `Transfer ${entity.title}` : 'Transfer',
        }}
      />
      <ContentBoundary
        data={
          entity?.title && recipient?.name
            ? {
                entity: { id: entity.id, title: entity.title },
                recipient: { id: recipient.id, name: recipient.name },
              }
            : null
        }
        loading={result.fetching}
        error={result.error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={
          <EmptyPlaceholder message="Ownership transfer is unavailable." />
        }
      >
        {({ entity, recipient }) => (
          <DestructiveAction
            key={`${entity.id}:${recipient.id}`}
            confirmationName={entity.title}
            label="Transfer ownership"
            submittingLabel="Transferring…"
            onAction={onTransfer}
            onSuccess={() => {
              back();
              back();
              back();
            }}
            testID="TeamTransferConfirmButton"
          >
            <DestructiveActionHeading>
              Transfer ownership of {entity.title} to {recipient.name}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This gives the recipient full control. You cannot undo this action
              yourself.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
